<template>
  <div :class="[style.inputSlider, style[`size${size}`]]">
    <span v-if="text" :class="style.label">
      {{ text }}
    </span>

    <div :class="style.content">
      <input
        :class="style.range"
        type="range"
        :min="min"
        :max="max"
        :value="normalizedValue"
        :style="rangeStyle"
        @input="handleRangeInput"
      />

      <InputStyle
        type="number"
        :min="min"
        :max="max"
        :size="size"
        :modelValue="String(normalizedValue)"
        :suffix="suffix"
        @update:modelValue="handleNumberInput"
        @blur="handleNumberBlur"
      />
    </div>
  </div>
</template>

<script lang="ts">
import {defineComponent, PropType} from "vue";
import style from "./InputSlider.module.scss";
import InputStyle from "@/elements/UI/input/Style/InputStyle.vue";

export default defineComponent({
  name: "InputSlider",

  components: {
    InputStyle
  },

  emits: ["update:modelValue", "change"],

  props: {
    modelValue: {
      type: Number,
      default: 0
    },

    text: {
      type: String,
      default: ""
    },

    min: {
      type: Number,
      default: 0
    },

    max: {
      type: Number,
      default: 100
    },

    size: {
      type: String as PropType<Size>,
      default: "M"
    },

    suffix: {
      type: String,
      default: ""
    }
  },

  data() {
    return {
      style
    };
  },

  computed: {
    normalizedValue(): number {
      return this.normalizeValue(Number(this.modelValue));
    },

    progressPercent(): number {
      if (this.max === this.min) {
        return 0;
      }

      return ((this.normalizedValue - this.min) / (this.max - this.min)) * 100;
    },

    rangeStyle(): Record<string, string> {
      return {
        "--progress-percent": `${this.progressPercent}%`
      };
    }
  },

  methods: {
    normalizeValue(value: number): number {
      if (!Number.isFinite(value)) {
        return this.min;
      }

      if (value < this.min) {
        return this.min;
      }

      if (value > this.max) {
        return this.max;
      }

      return value;
    },

    emitValue(value: number): void {
      const normalizedValue = this.normalizeValue(value);

      this.$emit("update:modelValue", normalizedValue);
      this.$emit("change", normalizedValue);
    },

    getValueFromPayload(payload: Event | string | number): number {
      if (typeof payload === "number") {
        return payload;
      }

      if (typeof payload === "string") {
        return Number(payload);
      }

      const target = payload.target as HTMLInputElement | null;

      return Number(target?.value ?? this.min);
    },

    handleRangeInput(event: Event): void {
      this.emitValue(this.getValueFromPayload(event));
    },

    handleNumberInput(payload: Event | string | number): void {
      this.emitValue(this.getValueFromPayload(payload));
    },

    handleNumberBlur(payload: Event | string | number): void {
      const value = this.getValueFromPayload(payload);
      this.emitValue(value);
    }
  }
});
</script>
